import { Flex } from "@chakra-ui/react";
import { useId } from "react";
import { FormattedMessage } from "react-intl";

import { LegendIcon } from "@nens/ranalib";
import { getWmsRequest } from "@nens/react-map-gl/utils";

import { LayerControl, LayerControlHeader } from "@/components/layer-control";
import { RanaLegend } from "@/lib/rana-legend";
import { RanaMap } from "@/lib/rana-map";
import { RanaPopup } from "@/lib/rana-popup/rana-popup";
import { ScenarioFileDescriptor } from "@/types/descriptor-types";

import { useScenarioPreview } from "../hooks/use-scenario-preview";

interface ProjectFilePreviewScenarioProps {
  title: string;
  descriptor: ScenarioFileDescriptor;
}

export function ProjectFilePreviewScenario({ title, descriptor }: ProjectFilePreviewScenarioProps) {
  const mapId = `scenario-map-${useId()}`;
  const {
    bounds,
    layers,
    disabledLayers,
    visibleLayers,
    setVisibleLayers,
    visibleLayersFromLayers,
    point,
    setPoint,
  } = useScenarioPreview(descriptor);

  return (
    <RanaMap
      mapId={mapId}
      bounds={bounds}
      mapProps={{
        onClick: (e) => setPoint(e.lngLat),
      }}
    >
      <RanaMap.MapScale />

      {layers.length && (
        <RanaMap.ControlsTopLeft>
          <LayerControlHeader />
          <LayerControl
            disabledLayers={disabledLayers}
            descriptorId={descriptor.id}
            title={title}
            layers={layers.map((layer) => layer.name)}
            visibleLayers={visibleLayers}
            setVisibleLayers={setVisibleLayers}
          />
        </RanaMap.ControlsTopLeft>
      )}

      {/* Legends */}
      {visibleLayersFromLayers.length > 0 && (
        <RanaMap.ControlsBottomLeft>
          <RanaMap.ControlsHeader>
            <LegendIcon color="brand.sky" />
            <FormattedMessage id="filepreview.legend" defaultMessage="Legend" />
          </RanaMap.ControlsHeader>
          <Flex flexDir="column" gap={2} p={2} minW={56} maxH="3xs" overflow="auto">
            {visibleLayersFromLayers.map(({ name, code, label }) => {
              return (
                <RanaLegend
                  key={code}
                  descriptorId={descriptor.id}
                  layer={code}
                  title={name}
                  unit={label ?? undefined}
                />
              );
            })}
          </Flex>
        </RanaMap.ControlsBottomLeft>
      )}

      {/* Raster layers */}
      {layers.map(({ name, code }) => {
        const url = getWmsRequest(
          `/v1-alpha/tenants/${descriptor.tenant}/file-descriptors/${descriptor.id}/wms`,
          {
            layer: code,
            transparent: true,
          }
        );
        return (
          <RanaMap.Source bounds={bounds} key={url} type="raster" tiles={[url]}>
            <RanaMap.Layer
              id={`scenario-raster-layer-${code}`}
              type="raster"
              isVisible={visibleLayers.some(({ layer }) => layer === name)}
            />
          </RanaMap.Source>
        );
      })}

      {!layers.length && <RanaMap.ExtentLayer bounds={bounds} />}

      {/* Popup info */}
      {visibleLayersFromLayers.some(({ id }) => id) && (
        <RanaPopup point={point} onClose={() => setPoint(null)}>
          <RanaPopup.ScenarioInfo
            layers={visibleLayersFromLayers}
            descriptorId={descriptor.id}
            point={point}
          />
        </RanaPopup>
      )}
    </RanaMap>
  );
}
